import { createListLinkTarget } from '../../utils/listPageState'

const EDIT_APP_BANNER_DRAFT_PREFIX = 'admin:appBannerDraft:edit:'
export const NEW_APP_BANNER_DRAFT_KEY = 'admin:appBannerDraft:new'
export const APP_BANNER_IMAGE_FILE_ERROR_MESSAGE = '이미지 파일만 업로드할 수 있습니다.'
export const APP_BANNER_IMAGE_FILE_ACCEPT = '.jpg,.jpeg,.png,.gif,.webp,image/png,image/gif,image/jpeg,image/webp'
export const APP_BANNER_IMAGE_FIELD_CONFIGS = [
    {
        fieldName: 'mainImage',
        imageType: 1,
        previewAlt: '메인이미지',
        uploadLabel: '메인이미지 업로드',
    },
    {
        fieldName: 'subImage',
        imageType: 2,
        previewAlt: '상세이미지',
        uploadLabel: '상세이미지 업로드',
    },
]

export const createDefaultAppBannerParams = () => ({
    cCode: 'kr',
    type: 1,
    title: '',
    content: '',
    image: '',
    url: '',
})

const getSessionStorage = () => {
    if (typeof window === 'undefined') {
        return null
    }

    return window.sessionStorage
}

const normalizeTextValue = (value) => {
    if (value === undefined || value === null) {
        return ''
    }

    return String(value)
}

const normalizeNumberValue = (value, fallbackValue) => {
    if (value === undefined || value === null || value === '') {
        return fallbackValue
    }

    const parsedValue = Number(value)

    return Number.isNaN(parsedValue) ? fallbackValue : parsedValue
}

export const applyAppBannerFieldChange = ({
    params = {},
    name,
    value,
}) => ({
    ...params,
    [name]: name === 'type' ? Number(value) : value,
})

// 저장 직전 제목/내용 정리와 필수값 검증을 한 기준으로 맞춘다.
export const prepareAppBannerSubmit = ({
    params = {},
    bannerId,
    requireMainImage = false,
}) => {
    const nextTitle = normalizeTextValue(params.title).trim()
    const nextContent = normalizeTextValue(params.content).trim()
    const nextParams = {
        ...params,
        title: nextTitle,
        content: nextContent,
    }

    if (!nextTitle) {
        return {
            nextParams,
            errorMessage: '제목을 입력하세요.',
        }
    }

    if (requireMainImage && !params.mainImage) {
        return {
            nextParams,
            errorMessage: '메인이미지를 업로드하세요.',
        }
    }

    const formData = new FormData()

    if (bannerId !== undefined) {
        formData.append('bannerId', bannerId)
    }

    formData.append('mainImage', params.mainImage)
    formData.append('subImage', params.subImage)
    formData.append('cCode', params.cCode)
    formData.append('type', params.type)
    formData.append('title', nextTitle)
    formData.append('content', nextContent)
    formData.append('url', params.url)

    return {
        nextParams,
        errorMessage: '',
        formData,
    }
}

// 목록 복귀 시 기존 필터/페이지를 유지하는 링크 생성 규칙을 한 곳에서 맞춘다.
export const createAppBannerListLinkTarget = ({
    listSearch = '',
    snackbarState,
    curPage,
    searchText,
    searchSelect,
}) => createListLinkTarget({
    pathname: '/appBanners',
    search: listSearch,
    ...(snackbarState ? {
        openSnackbar: true,
        severitySnackbar: snackbarState.severity,
        messageSnackbar: snackbarState.message,
    } : {}),
    curPage,
    searchText,
    searchSelect,
})

export const moveToAppBannerList = ({
    draftKey,
    clearDraft,
    setNavigationUnlocked,
    history,
    listSearch,
    snackbarState,
    curPage,
    searchText,
    searchSelect,
}) => {
    setNavigationUnlocked(true)
    clearDraft(draftKey)

    setTimeout(() => {
        history.push(createAppBannerListLinkTarget({
            listSearch,
            snackbarState,
            curPage,
            searchText,
            searchSelect,
        }))
    }, 0)
}

export const handleAppBannerBackNavigation = ({
    event,
    leaveGuardEnabled,
    draftKey,
    clearDraft,
    confirmLeave,
    moveToList,
}) => {
    if (!leaveGuardEnabled) {
        clearDraft(draftKey)
        return
    }

    event.preventDefault()

    if (!confirmLeave()) {
        return
    }

    moveToList()
}

const isAppBannerImageFile = (file) => {
    return Boolean(file) && (
        (file.type && file.type.startsWith('image/')) ||
        /\.(jpg|jpeg|png|gif|webp)$/i.test(file.name || '')
    )
}

// App 배너 이미지 업로드는 파일 검증과 preview 갱신 규칙을 한 곳에서 맞춘다.
export const handleAppBannerImageUpload = ({
    event,
    imageType,
    setParams,
    setMainImage,
    setSubImage,
    onInvalidFile,
}) => {
    event.preventDefault()

    const { name, files } = event.target
    const input = event.target
    const file = files[0]

    if (!file) {
        return
    }

    if (!isAppBannerImageFile(file)) {
        input.value = ''
        onInvalidFile(APP_BANNER_IMAGE_FILE_ERROR_MESSAGE)
        return
    }

    const reader = new FileReader()

    reader.onloadend = () => {
        setParams(prev => ({
            ...prev,
            [name]: file,
        }))

        if (imageType === 1) {
            setMainImage(reader.result)
            return
        }

        if (imageType === 2) {
            setSubImage(reader.result)
        }
    }

    reader.readAsDataURL(file)
}

// App 배너 폼은 이미지 파일을 제외한 선택/텍스트 필드만 sessionStorage에 남긴다.
export const createAppBannerDraftPayload = ({ params = {} } = {}) => {
    return {
        params: {
            cCode: normalizeTextValue(params.cCode || 'kr'),
            type: normalizeNumberValue(params.type, 1),
            title: normalizeTextValue(params.title),
            content: normalizeTextValue(params.content),
            url: normalizeTextValue(params.url),
        }
    }
}

// 서버 기본값 위에 draft만 덮어써서 새로고침 뒤에도 App 배너 작성 흐름을 이어간다.
export const applyAppBannerDraft = ({ baseParams = {}, draft }) => {
    const normalizedBaseDraft = createAppBannerDraftPayload({
        params: baseParams,
    })

    if (!draft) {
        return {
            params: {
                ...baseParams,
                ...normalizedBaseDraft.params,
            },
        }
    }

    const draftParams = draft.params || {}

    return {
        params: {
            ...baseParams,
            cCode: draftParams.cCode === undefined
                ? normalizedBaseDraft.params.cCode
                : normalizeTextValue(draftParams.cCode),
            type: draftParams.type === undefined
                ? normalizedBaseDraft.params.type
                : normalizeNumberValue(draftParams.type, normalizedBaseDraft.params.type),
            title: draftParams.title === undefined
                ? normalizedBaseDraft.params.title
                : normalizeTextValue(draftParams.title),
            content: draftParams.content === undefined
                ? normalizedBaseDraft.params.content
                : normalizeTextValue(draftParams.content),
            url: draftParams.url === undefined
                ? normalizedBaseDraft.params.url
                : normalizeTextValue(draftParams.url),
        }
    }
}

export const createEditAppBannerDraftKey = (bannerId) => `${EDIT_APP_BANNER_DRAFT_PREFIX}${bannerId}`

export const readAppBannerDraft = (draftKey) => {
    const storage = getSessionStorage()

    if (!storage || !draftKey) {
        return null
    }

    try {
        const storedValue = storage.getItem(draftKey)
        return storedValue ? JSON.parse(storedValue) : null
    } catch (_error) {
        return null
    }
}

export const saveAppBannerDraft = (draftKey, draftValue) => {
    const storage = getSessionStorage()

    if (!storage || !draftKey) {
        return
    }

    try {
        storage.setItem(draftKey, JSON.stringify(draftValue))
    } catch (_error) {
        // sessionStorage가 막혀 있어도 작성 흐름은 그대로 유지한다.
    }
}

export const clearAppBannerDraft = (draftKey) => {
    const storage = getSessionStorage()

    if (!storage || !draftKey) {
        return
    }

    try {
        storage.removeItem(draftKey)
    } catch (_error) {
        // storage 정리에 실패해도 화면 동작은 계속 진행한다.
    }
}
